import { z } from "zod";
import { VERSION } from "../../version.js";
import { generateJWT } from "../jwt.js";
import { BatchTaskRunExecutionResult, BatchTriggerTaskV2Response, CanceledRunResponse, CreateUploadPayloadUrlResponseBody, DeletedScheduleObject, EnvironmentVariableResponseBody, EnvironmentVariableValue, EnvironmentVariables, ListRunResponseItem, ReplayRunResponse, RetrieveBatchResponse, RetrieveRunResponse, ScheduleObject, TaskRunExecutionResult, TriggerTaskResponse, UpdateMetadataResponseBody, } from "../schemas/index.js";
import { taskContext } from "../task-context-api.js";
import { isRequestOptions, zodfetch, zodfetchCursorPage, zodfetchOffsetLimitPage, } from "./core.js";
import { ApiError } from "./errors.js";
import { runShapeStream, SSEStreamSubscriptionFactory, } from "./runStream.js";
const DEFAULT_ZOD_FETCH_OPTIONS = {
    retry: {
        maxAttempts: 5,
        minTimeoutInMs: 1000,
        maxTimeoutInMs: 30_000,
        factor: 1.6,
        randomize: false,
    },
};
export { isRequestOptions };
/**
 * Trigger.dev v3 API client
 */
export class ApiClient {
    baseUrl;
    accessToken;
    defaultRequestOptions;
    constructor(baseUrl, accessToken, requestOptions = {}) {
        this.accessToken = accessToken;
        this.baseUrl = baseUrl.replace(/\/$/, "");
        this.defaultRequestOptions = mergeRequestOptions(DEFAULT_ZOD_FETCH_OPTIONS, requestOptions);
    }
    get fetchClient() {
        const headers = this.#getHeaders(false);
        const fetchClient = (input, requestInit) => {
            const $requestInit = {
                ...requestInit,
                headers: {
                    ...requestInit?.headers,
                    ...headers,
                },
            };
            return fetch(input, $requestInit);
        };
        return fetchClient;
    }
    getHeaders() {
        return this.#getHeaders(false);
    }
    async getRunResult(runId, requestOptions) {
        try {
            return await zodfetch(TaskRunExecutionResult, `${this.baseUrl}/api/v1/runs/${runId}/result`, {
                method: "GET",
                headers: this.#getHeaders(false),
            }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
        }
        catch (error) {
            if (error instanceof ApiError) {
                if (error.status === 404) {
                    return undefined;
                }
            }
            throw error;
        }
    }
    async getBatchResults(batchId, requestOptions) {
        return await zodfetch(BatchTaskRunExecutionResult, `${this.baseUrl}/api/v1/batches/${batchId}/results`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    triggerTask(taskId, body, clientOptions, requestOptions) {
        const encodedTaskId = encodeURIComponent(taskId);
        return zodfetch(TriggerTaskResponse, `${this.baseUrl}/api/v1/tasks/${encodedTaskId}/trigger`, {
            method: "POST",
            headers: this.#getHeaders(clientOptions?.spanParentAsLink ?? false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions))
            .withResponse()
            .then(async ({ response, data }) => {
            const jwtHeader = response.headers.get("x-trigger-jwt");
            if (typeof jwtHeader === "string") {
                return {
                    ...data,
                    publicAccessToken: jwtHeader,
                };
            }
            const claimsHeader = response.headers.get("x-trigger-jwt-claims");
            const claims = claimsHeader ? JSON.parse(claimsHeader) : undefined;
            const jwt = await generateJWT({
                secretKey: this.accessToken,
                payload: {
                    ...claims,
                    scopes: [`read:runs:${data.id}`],
                },
                expirationTime: requestOptions?.publicAccessToken?.expirationTime ?? "1h",
            });
            return {
                ...data,
                publicAccessToken: jwt,
            };
        });
    }
    batchTriggerV2(body, clientOptions, requestOptions) {
        return zodfetch(BatchTriggerTaskV2Response, `${this.baseUrl}/api/v1/tasks/batch`, {
            method: "POST",
            headers: this.#getHeaders(clientOptions?.spanParentAsLink ?? false, {
                "idempotency-key": clientOptions?.idempotencyKey,
                "idempotency-key-ttl": clientOptions?.idempotencyKeyTTL,
                "batch-processing-strategy": clientOptions?.processingStrategy,
            }),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions))
            .withResponse()
            .then(async ({ response, data }) => {
            const claimsHeader = response.headers.get("x-trigger-jwt-claims");
            const claims = claimsHeader ? JSON.parse(claimsHeader) : undefined;
            const jwt = await generateJWT({
                secretKey: this.accessToken,
                payload: {
                    ...claims,
                    scopes: [`read:batch:${data.id}`],
                },
                expirationTime: requestOptions?.publicAccessToken?.expirationTime ?? "1h",
            });
            return {
                ...data,
                publicAccessToken: jwt,
            };
        });
    }
    createUploadPayloadUrl(filename, requestOptions) {
        return zodfetch(CreateUploadPayloadUrlResponseBody, `${this.baseUrl}/api/v1/packets/${filename}`, {
            method: "PUT",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    getPayloadUrl(filename, requestOptions) {
        return zodfetch(CreateUploadPayloadUrlResponseBody, `${this.baseUrl}/api/v1/packets/${filename}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    retrieveRun(runId, requestOptions) {
        return zodfetch(RetrieveRunResponse, `${this.baseUrl}/api/v3/runs/${runId}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    listRuns(query, requestOptions) {
        const searchParams = createSearchQueryForListRuns(query);
        return zodfetchCursorPage(ListRunResponseItem, `${this.baseUrl}/api/v1/runs`, {
            query: searchParams,
            limit: query?.limit,
            after: query?.after,
            before: query?.before,
        }, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    listProjectRuns(projectRef, query, requestOptions) {
        const searchParams = createSearchQueryForListRuns(query);
        if (query?.env) {
            searchParams.append("filter[env]", Array.isArray(query.env) ? query.env.join(",") : query.env);
        }
        return zodfetchCursorPage(ListRunResponseItem, `${this.baseUrl}/api/v1/projects/${projectRef}/runs`, {
            query: searchParams,
            limit: query?.limit,
            after: query?.after,
            before: query?.before,
        }, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    replayRun(runId, requestOptions) {
        return zodfetch(ReplayRunResponse, `${this.baseUrl}/api/v1/runs/${runId}/replay`, {
            method: "POST",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    cancelRun(runId, requestOptions) {
        return zodfetch(CanceledRunResponse, `${this.baseUrl}/api/v2/runs/${runId}/cancel`, {
            method: "POST",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    rescheduleRun(runId, body, requestOptions) {
        return zodfetch(RetrieveRunResponse, `${this.baseUrl}/api/v1/runs/${runId}/reschedule`, {
            method: "POST",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    addTags(runId, body, requestOptions) {
        return zodfetch(z.object({ message: z.string() }), `${this.baseUrl}/api/v1/runs/${runId}/tags`, {
            method: "POST",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    createSchedule(options, requestOptions) {
        return zodfetch(ScheduleObject, `${this.baseUrl}/api/v1/schedules`, {
            method: "POST",
            headers: this.#getHeaders(false),
            body: JSON.stringify(options),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    listSchedules(options, requestOptions) {
        const searchParams = new URLSearchParams();
        if (options?.page) {
            searchParams.append("page", options.page.toString());
        }
        if (options?.perPage) {
            searchParams.append("perPage", options.perPage.toString());
        }
        return zodfetchOffsetLimitPage(ScheduleObject, `${this.baseUrl}/api/v1/schedules`, {
            page: options?.page,
            limit: options?.perPage,
        }, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    retrieveSchedule(scheduleId, requestOptions) {
        return zodfetch(ScheduleObject, `${this.baseUrl}/api/v1/schedules/${scheduleId}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    updateSchedule(scheduleId, options, requestOptions) {
        return zodfetch(ScheduleObject, `${this.baseUrl}/api/v1/schedules/${scheduleId}`, {
            method: "PUT",
            headers: this.#getHeaders(false),
            body: JSON.stringify(options),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    deactivateSchedule(scheduleId, requestOptions) {
        return zodfetch(ScheduleObject, `${this.baseUrl}/api/v1/schedules/${scheduleId}/deactivate`, {
            method: "POST",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    activateSchedule(scheduleId, requestOptions) {
        return zodfetch(ScheduleObject, `${this.baseUrl}/api/v1/schedules/${scheduleId}/activate`, {
            method: "POST",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    deleteSchedule(scheduleId, requestOptions) {
        return zodfetch(DeletedScheduleObject, `${this.baseUrl}/api/v1/schedules/${scheduleId}`, {
            method: "DELETE",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    listEnvVars(projectRef, slug, requestOptions) {
        return zodfetch(EnvironmentVariables, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    importEnvVars(projectRef, slug, body, requestOptions) {
        return zodfetch(EnvironmentVariableResponseBody, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}/import`, {
            method: "POST",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    retrieveEnvVar(projectRef, slug, key, requestOptions) {
        return zodfetch(EnvironmentVariableValue, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}/${key}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    createEnvVar(projectRef, slug, body, requestOptions) {
        return zodfetch(EnvironmentVariableResponseBody, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}`, {
            method: "POST",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    updateEnvVar(projectRef, slug, key, body, requestOptions) {
        return zodfetch(EnvironmentVariableResponseBody, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}/${key}`, {
            method: "PUT",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    deleteEnvVar(projectRef, slug, key, requestOptions) {
        return zodfetch(EnvironmentVariableResponseBody, `${this.baseUrl}/api/v1/projects/${projectRef}/envvars/${slug}/${key}`, {
            method: "DELETE",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    updateRunMetadata(runId, body, requestOptions) {
        return zodfetch(UpdateMetadataResponseBody, `${this.baseUrl}/api/v1/runs/${runId}/metadata`, {
            method: "PUT",
            headers: this.#getHeaders(false),
            body: JSON.stringify(body),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    getRunMetadata(runId, requestOptions) {
        return zodfetch(UpdateMetadataResponseBody, `${this.baseUrl}/api/v1/runs/${runId}/metadata`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    subscribeToRun(runId, options) {
        return runShapeStream(`${this.baseUrl}/realtime/v1/runs/${runId}`, {
            closeOnComplete: typeof options?.closeOnComplete === "boolean" ? options.closeOnComplete : true,
            headers: this.#getRealtimeHeaders(),
            client: this,
            signal: options?.signal,
            onFetchError: options?.onFetchError,
        });
    }
    subscribeToRunsWithTag(tag, options) {
        const searchParams = createSearchQueryForSubscribeToRuns({
            tags: tag,
        });
        return runShapeStream(`${this.baseUrl}/realtime/v1/runs${searchParams ? `?${searchParams}` : ""}`, {
            closeOnComplete: false,
            headers: this.#getRealtimeHeaders(),
            client: this,
            signal: options?.signal,
            onFetchError: options?.onFetchError,
        });
    }
    subscribeToBatch(batchId, options) {
        return runShapeStream(`${this.baseUrl}/realtime/v1/batches/${batchId}`, {
            closeOnComplete: false,
            headers: this.#getRealtimeHeaders(),
            client: this,
            signal: options?.signal,
            onFetchError: options?.onFetchError,
        });
    }
    async fetchStream(runId, streamKey, options) {
        const streamFactory = new SSEStreamSubscriptionFactory(options?.baseUrl ?? this.baseUrl, {
            headers: this.getHeaders(),
            signal: options?.signal,
        });
        const subscription = streamFactory.createSubscription(runId, streamKey);
        const stream = await subscription.subscribe();
        return stream;
    }
    async generateJWTClaims(requestOptions) {
        return zodfetch(z.record(z.any()), `${this.baseUrl}/api/v1/auth/jwt/claims`, {
            method: "POST",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    retrieveBatch(batchId, requestOptions) {
        return zodfetch(RetrieveBatchResponse, `${this.baseUrl}/api/v1/batches/${batchId}`, {
            method: "GET",
            headers: this.#getHeaders(false),
        }, mergeRequestOptions(this.defaultRequestOptions, requestOptions));
    }
    #getHeaders(spanParentAsLink, additionalHeaders) {
        const headers = {
            "Content-Type": "application/json",
            Authorization: `Bearer ${this.accessToken}`,
            "trigger-version": VERSION,
            ...Object.entries(additionalHeaders ?? {}).reduce((acc, [key, value]) => {
                if (value !== undefined) {
                    acc[key] = value;
                }
                return acc;
            }, {}),
        };
        // Only inject the context if we are inside a task
        if (taskContext.isInsideTask) {
            headers["x-trigger-worker"] = "true";
            if (spanParentAsLink) {
                headers["x-trigger-span-parent-as-link"] = "1";
            }
        }
        if (typeof window !== "undefined" && typeof window.document !== "undefined") {
            headers["x-trigger-client"] = "browser";
        }
        return headers;
    }
    #getRealtimeHeaders() {
        const headers = {
            Authorization: `Bearer ${this.accessToken}`,
            "trigger-version": VERSION,
        };
        return headers;
    }
}
function createSearchQueryForSubscribeToRuns(query) {
    const searchParams = new URLSearchParams();
    if (query) {
        if (query.tasks) {
            searchParams.append("tasks", Array.isArray(query.tasks) ? query.tasks.join(",") : query.tasks);
        }
        if (query.tags) {
            searchParams.append("tags", Array.isArray(query.tags) ? query.tags.join(",") : query.tags);
        }
    }
    return searchParams;
}
function createSearchQueryForListRuns(query) {
    const searchParams = new URLSearchParams();
    if (query) {
        if (query.status) {
            searchParams.append("filter[status]", Array.isArray(query.status) ? query.status.join(",") : query.status);
        }
        if (query.taskIdentifier) {
            searchParams.append("filter[taskIdentifier]", Array.isArray(query.taskIdentifier) ? query.taskIdentifier.join(",") : query.taskIdentifier);
        }
        if (query.version) {
            searchParams.append("filter[version]", Array.isArray(query.version) ? query.version.join(",") : query.version);
        }
        if (query.bulkAction) {
            searchParams.append("filter[bulkAction]", query.bulkAction);
        }
        if (query.tag) {
            searchParams.append("filter[tag]", Array.isArray(query.tag) ? query.tag.join(",") : query.tag);
        }
        if (query.schedule) {
            searchParams.append("filter[schedule]", query.schedule);
        }
        if (typeof query.isTest === "boolean") {
            searchParams.append("filter[isTest]", String(query.isTest));
        }
        if (query.from) {
            searchParams.append("filter[createdAt][from]", query.from instanceof Date ? query.from.getTime().toString() : query.from.toString());
        }
        if (query.to) {
            searchParams.append("filter[createdAt][to]", query.to instanceof Date ? query.to.getTime().toString() : query.to.toString());
        }
        if (query.period) {
            searchParams.append("filter[createdAt][period]", query.period);
        }
        if (query.batch) {
            searchParams.append("filter[batch]", query.batch);
        }
    }
    return searchParams;
}
export function mergeRequestOptions(defaultOptions, options) {
    if (!options) {
        return defaultOptions;
    }
    return {
        ...defaultOptions,
        ...options,
        retry: {
            ...defaultOptions.retry,
            ...options.retry,
        },
    };
}
//# sourceMappingURL=index.js.map